import React, { useEffect, useMemo, useRef } from 'react';
import { useConfigurationDrawer } from '../context';
import { ChatMessages } from './ChatMessages';
import { IAssistantMessage } from '../types';

/**
 * Modal Chatbot Component
 *
 * Displays the chatbot interface for editing window configurations
 * Uses ConfigurationDrawerContext for state and methods
 */
export const ModalChatbot: React.FC = () => {
    const { state, method } = useConfigurationDrawer();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [state.draftMessages]);

    // Merge original item messages with draft messages so fire-egress extraction
    // has the full conversation context. Draft answers take priority over originals.
    const contextMessages = useMemo<IAssistantMessage[]>(() => {
        const draftQuestionIds = new Set(
            state.draftMessages
                .filter((m) => m.question?.questionId)
                .map((m) => m.question.questionId)
        );
        const originalFiltered = (state.editingItem?.messages ?? []).filter(
            (m) => !m.question?.questionId || !draftQuestionIds.has(m.question.questionId)
        );
        return [...originalFiltered, ...state.draftMessages];
    }, [state.editingItem?.messages, state.draftMessages]);

    return (
        <div className="flex flex-col h-full">
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4">
                <ChatMessages
                    messages={state.draftMessages}
                    contextMessages={contextMessages}
                    selectedProduct={state.selectedProduct}
                    validationRules={state.config.validationRules}
                    isEditMode={true}
                    egressConsentState={state.egressConsentState}
                    onEgressConsentAccept={method.acceptEgressConsent}
                    onEgressConsentReject={method.rejectEgressConsent}
                    handleSingleSelect={method.handleSingleSelect}
                    updateSingleSelect={method.handleSingleSelect}
                    handleMultipleChoice={method.handleMultipleChoice}
                    updateMultipleChoice={method.handleMultipleChoice}
                    handleFormSubmit={method.handleFormSubmit}
                    updateFormSubmit={method.handleFormSubmit}
                    handleTextInput={method.handleTextInput}
                    updateTextInput={method.handleTextInput}
                />
                <div ref={messagesEndRef} />
            </div>
        </div>
    );
};
